import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Input,
  Select,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useEffect, useMemo, useState } from 'react';

import { getPatentDocuments } from './api';
import { DIRECTION_OPTIONS } from './constants';
import { PatentDocumentsList } from './PatentDocumentsList';

import type { PatentDocument } from './types';

interface PatentDocumentsSearchProps {
  applicationNumber: string | null;
  searchTrigger: number;
  onSearchingChange?: (_isSearching: boolean) => void;
}

export const PatentDocumentsSearch = ({
  applicationNumber,
  searchTrigger,
  onSearchingChange,
}: PatentDocumentsSearchProps) => {
  const [isSearching, setIsSearching] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<PatentDocument[] | null>(null);

  const [directionFilter, setDirectionFilter] = useState('');
  const [mimeTypeFilter, setMimeTypeFilter] = useState('');
  const [documentTextFilter, setDocumentTextFilter] = useState('');

  const mimeTypeOptions = useMemo(() => {
    const discoveredMimeTypes = new Set<string>();
    (documents || []).forEach((document) => {
      (document.downloadOptionBag || []).forEach((option) => {
        if (option.mimeTypeIdentifier) {
          discoveredMimeTypes.add(option.mimeTypeIdentifier);
        }
      });
    });
    const sorted = Array.from(discoveredMimeTypes).sort();
    return [
      { value: '', display: 'All File Types' },
      ...sorted.map((mimeType) => ({
        value: mimeType,
        display: mimeType,
      })),
    ];
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const normalizedTextFilter = documentTextFilter.trim().toLowerCase();

    return (documents || []).filter((document) => {
      if (
        directionFilter &&
        (document.directionCategory || '') !== directionFilter
      ) {
        return false;
      }

      if (mimeTypeFilter) {
        const hasMimeType = (document.downloadOptionBag || []).some(
          (option) => option.mimeTypeIdentifier === mimeTypeFilter
        );
        if (!hasMimeType) {
          return false;
        }
      }

      if (!normalizedTextFilter) {
        return true;
      }

      const searchableText = [
        document.documentCodeDescriptionText,
        document.documentCode,
        document.documentIdentifier,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedTextFilter);
    });
  }, [documents, directionFilter, mimeTypeFilter, documentTextFilter]);

  const clearFilters = () => {
    setDirectionFilter('');
    setMimeTypeFilter('');
    setDocumentTextFilter('');
  };

  useEffect(() => {
    if (!applicationNumber) return;

    let cancelled = false;

    setIsSearching(true);
    setDocumentsError(null);
    setDocuments(null);
    clearFilters();
    onSearchingChange?.(true);

    getPatentDocuments(applicationNumber)
      .then((response) => {
        if (!cancelled) {
          setDocuments(response.documentBag || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDocumentsError(
            error instanceof Error
              ? error.message
              : 'Unable to load patent documents'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSearching(false);
          onSearchingChange?.(false);
        }
      });

    return () => {
      cancelled = true;
      onSearchingChange?.(false);
    };
    // searchTrigger allows re-searching the same applicationNumber
  }, [applicationNumber, searchTrigger, onSearchingChange]);

  if (
    !applicationNumber &&
    !isSearching &&
    documents === null &&
    !documentsError
  ) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {documentsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {documentsError}
        </Alert>
      )}

      {isSearching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress />
            <Text variant="body2" color="text.secondary">
              Loading documents...
            </Text>
          </Stack>
        </Box>
      )}

      {!isSearching && documents && (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Stack spacing={2}>
            <Text variant="body2" sx={{ fontWeight: 600 }}>
              Documents ({documents.length})
            </Text>

            {documents.length > 0 && (
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Text
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      Direction
                    </Text>
                    <Select
                      id="documents-direction-filter"
                      value={directionFilter}
                      options={DIRECTION_OPTIONS}
                      onOptionSelect={setDirectionFilter}
                      sx={{ width: '100%' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Text
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      File Type
                    </Text>
                    <Select
                      id="documents-mime-filter"
                      value={mimeTypeFilter}
                      options={mimeTypeOptions}
                      onOptionSelect={setMimeTypeFilter}
                      sx={{ width: '100%' }}
                    />
                  </Box>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <Input
                    size="small"
                    fullWidth
                    placeholder="Filter by code, description, or identifier"
                    aria-label="Filter documents"
                    value={documentTextFilter}
                    onChange={(e) => setDocumentTextFilter(e.target.value)}
                  />
                  <Button variant="text" size="small" onClick={clearFilters}>
                    Clear
                  </Button>
                </Stack>
              </Stack>
            )}

            {documents.length === 0 && (
              <Text variant="body2" color="text.secondary">
                No documents are available for this application.
              </Text>
            )}

            {documents.length > 0 && filteredDocuments.length === 0 && (
              <Text variant="body2" color="text.secondary">
                No documents match the selected filters.
              </Text>
            )}

            {filteredDocuments.length > 0 && (
              <PatentDocumentsList documents={filteredDocuments} />
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
